import { useState, useCallback, useEffect, useRef } from 'react';
import { apiClient } from '../lib/apiClient';

interface ReactionCounts {
  likes: number;
  dislikes: number;
  userReaction: 'like' | 'dislike' | null;
}

export const useVideoReactions = (videoId: string) => {
  const [reactionCounts, setReactionCounts] = useState<ReactionCounts>({
    likes: 0,
    dislikes: 0,
    userReaction: null,
  });
  // Separate loading states: initial load (blocks UI) vs background poll (silent).
  const [initialLoading, setInitialLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Guards against concurrent toggles.
  const toggleLockRef = useRef(false);

  // Background fetch — does NOT reset userReaction if the server omits it,
  // and does NOT block the UI (no loading spinner).
  const fetchReactionCounts = useCallback(async (isInitial = false) => {
    if (!videoId) return;
    try {
      if (isInitial) setInitialLoading(true);
      const { data, error: fetchError } = await apiClient.getReactions(videoId);
      if (fetchError) throw fetchError;
      if (data) {
        setReactionCounts(prev => ({
          likes: Number(data.like) || 0,
          dislikes: Number(data.dislike) || 0,
          // Preserve locally known userReaction when server returns null/undefined
          // (e.g. backend returns no user_reaction for anonymous polls).
          userReaction: data.user_reaction !== undefined ? data.user_reaction : prev.userReaction,
        }));
      }
    } catch (err) {
      console.error('Error fetching reaction counts:', err);
      if (isInitial) setError('Failed to load reactions');
    } finally {
      if (isInitial) setInitialLoading(false);
    }
  }, [videoId]);

  useEffect(() => {
    fetchReactionCounts(true);
    // Background poll every 30s — silent, does not disable buttons.
    const interval = setInterval(() => fetchReactionCounts(false), 30_000);
    return () => clearInterval(interval);
  }, [videoId, fetchReactionCounts]);

  const toggleReaction = useCallback(async (reactionType: 'like' | 'dislike') => {
    if (toggleLockRef.current) return; // prevent double-tap
    toggleLockRef.current = true;
    setToggling(true);

    // Optimistic update.
    let previousState: ReactionCounts | null = null;
    setReactionCounts(prev => {
      previousState = prev;
      if (prev.userReaction === reactionType) {
        // Deselect: remove the reaction.
        return {
          likes: reactionType === 'like' ? Math.max(0, prev.likes - 1) : prev.likes,
          dislikes: reactionType === 'dislike' ? Math.max(0, prev.dislikes - 1) : prev.dislikes,
          userReaction: null,
        };
      }
      // Switch or new selection.
      return {
        likes: reactionType === 'like'
          ? prev.likes + 1
          : prev.userReaction === 'like' ? Math.max(0, prev.likes - 1) : prev.likes,
        dislikes: reactionType === 'dislike'
          ? prev.dislikes + 1
          : prev.userReaction === 'dislike' ? Math.max(0, prev.dislikes - 1) : prev.dislikes,
        userReaction: reactionType,
      };
    });

    try {
      const { data, error: toggleError } = await apiClient.toggleReaction(videoId, reactionType);
      if (toggleError) throw toggleError;
      // Confirm with server state.
      if (data) {
        setReactionCounts({
          likes: Number(data.like) || 0,
          dislikes: Number(data.dislike) || 0,
          userReaction: data.user_reaction ?? null,
        });
      }
    } catch (err) {
      console.error('Error toggling reaction:', err);
      setError('Failed to update reaction');
      // Revert optimistic update.
      if (previousState) setReactionCounts(previousState);
    } finally {
      setToggling(false);
      toggleLockRef.current = false;
    }
  }, [videoId]);

  return {
    ...reactionCounts,
    loading: initialLoading,
    toggling,
    error,
    toggleReaction,
    refetch: () => fetchReactionCounts(false),
  };
};
